import { Trans } from "@lingui/macro";
import { UiAlert, UiBox, UiTypography } from "@smp/ui";
import { numberBitGetByPosition, textCurrencySymbolGet } from "@smp/utils";
import parseISO from "date-fns/parseISO";

import { useEpBillingPlansubscriptionList } from "@/hooks/use_ep_billing_plansubscription";
import { useEpBillingRecurringPaymentList } from "@/hooks/use_ep_billing_recurring_payment";
import { useHumanizer } from "@/hooks/use_humanizer";

import { useContextDialogPurchase } from "../../../context";

export const Warnings = () => {
  const {
    state: { paymentTypeSelected, productSelected, productTrial },
  } = useContextDialogPurchase();

  const { plansubscriptionList } = useEpBillingPlansubscriptionList({
    req: { query: { ordering: "-period_end" } },
  });

  const { recurringPaymentList } = useEpBillingRecurringPaymentList({
    req: { query: { ordering: "-created_at" } },
  });

  const { humanizeBillingPlanId, humanizePeriodUnit } = useHumanizer();

  const plansubscriptionLast = plansubscriptionList
    ? plansubscriptionList[0]
    : undefined;

  const plansubscriptionActive = plansubscriptionLast
    ? parseISO(plansubscriptionLast.period_end).getTime() >=
      new Date().getTime()
      ? plansubscriptionLast
      : undefined
    : undefined;

  const recurringPaymentActive = recurringPaymentList?.find(
    (recurringPayment) => !!numberBitGetByPosition(recurringPayment.status, 1),
  );

  const isWarningPaidPeriodVisible =
    paymentTypeSelected === "recurring" &&
    !recurringPaymentActive &&
    !!plansubscriptionActive;

  const isWarningRecurringPaymentTrialVisible = !!productTrial;

  const isWarningRecurringPaymentActiveProductSameVisible =
    paymentTypeSelected === "recurring" &&
    recurringPaymentActive &&
    recurringPaymentActive.product_regular === productSelected.id;

  const isWarningRecurringPaymentActiveProductNotSameVisible =
    paymentTypeSelected === "recurring" &&
    recurringPaymentActive &&
    recurringPaymentActive.product_regular !== productSelected.id;

  const isAnyWarningVisible =
    isWarningPaidPeriodVisible ||
    isWarningRecurringPaymentActiveProductNotSameVisible ||
    isWarningRecurringPaymentActiveProductSameVisible ||
    isWarningRecurringPaymentTrialVisible;

  if (productSelected.type !== "plan-product") {
    return null;
  }

  if (!isAnyWarningVisible) {
    return null;
  }

  return (
    <>
      {isWarningPaidPeriodVisible && (
        <UiBox mb={2}>
          <UiAlert variant="warning">
            <Trans>
              If after your PRO Plan expires you still have paid days, they will
              be saved, and used when you activate a new subscription. The
              remainder will be used only after the expiration of the
              subscription. You can check available days in the Subscription
              settings.
            </Trans>
          </UiAlert>
        </UiBox>
      )}
      {isWarningRecurringPaymentActiveProductNotSameVisible && (
        <UiBox mb={2}>
          <UiAlert variant="warning">
            <Trans>
              If you change your Subscription Plan, paid days will be
              recalculated and converted into the new Subscription Plan. If the
              only change is the payment method, no conversion takes place. The
              remaining days will be used only after the expiration of the paid
              subscription.
            </Trans>
          </UiAlert>
        </UiBox>
      )}
      {isWarningRecurringPaymentActiveProductSameVisible && (
        <UiBox mb={2}>
          <UiAlert variant="warning">
            <Trans>
              If you change your Subscription Plan, paid days will be
              recalculated and converted into the new Subscription Plan. If the
              only change is the payment method, no conversion takes place. The
              remaining days will be used only after the expiration of the paid
              subscription.
            </Trans>
          </UiAlert>
        </UiBox>
      )}
      {isWarningRecurringPaymentTrialVisible && (
        <UiBox mb={2}>
          <UiAlert variant="warning">
            <UiTypography>
              <Trans>
                {humanizeBillingPlanId(productTrial.plan.id)} Subscription Plan
                will be activated for{" "}
                {humanizePeriodUnit(
                  productTrial.period_units,
                  productTrial.period_length,
                )}
                .
              </Trans>
            </UiTypography>
            <UiTypography>
              <Trans>
                After the trial period, the{" "}
                {humanizeBillingPlanId(productSelected.plan.id)} Subscription
                Plan will be activated with a{" "}
                {humanizePeriodUnit(
                  productSelected.period_units,
                  productSelected.period_length,
                )}{" "}
                charge period, for {productSelected.price}{" "}
                {textCurrencySymbolGet(productSelected.currency)}.
              </Trans>
            </UiTypography>
            <UiTypography>
              <Trans>
                The setup payment will be charged when the trial period is
                activated (and will be refunded immediately).
              </Trans>
            </UiTypography>
          </UiAlert>
        </UiBox>
      )}
    </>
  );
};
